import apiClient, {
  refreshCookieAuthSession,
  requestCookieLogout,
} from "@/lib/services/apiClient"; // Use shared apiClient
import type {
  AuthRefreshSessionInvalidationContract,
  AuthErrorResponse,
  AuthResponse,
  AuthUser,
  ForgotPasswordResponse,
  RefreshTokenResponse,
} from "@/lib/types/auth";

export type User = AuthUser;
export type ErrorResponse = AuthErrorResponse;
// Wave-4 auth backend sync marker (refresh fail-close session invalidation).
// Wave-5 OAuth bootstrap sync marker: backend redirects to /dashboard?auth=google on successful OAuth callback.
export type { AuthRefreshSessionInvalidationContract, AuthResponse };

type ApiErrorShape = {
  response?: {
    status?: number;
    data?: Partial<ErrorResponse> & { details?: unknown[] };
  };
  message?: string;
};

export class AuthApiError extends Error {
  status?: number;
  code?: string;
  requiresVerification?: boolean;

  constructor(
    message: string,
    opts?: { status?: number; code?: string; requiresVerification?: boolean }
  ) {
    super(message);
    this.name = "AuthApiError";
    this.status = opts?.status;
    this.code = opts?.code;
    this.requiresVerification = opts?.requiresVerification;
  }
}

function mapLoginError(error: unknown): AuthApiError {
  const err = error as ApiErrorShape;
  const status = err?.response?.status;
  const data = err?.response?.data;
  // Wave-3 auth hardening sync: backend login/logout rate-limit and refresh guard changes.
  const requiresVerification = Boolean(
    (data as { requiresVerification?: boolean })?.requiresVerification
  );
  const code = data?.code;

  if (status === 429) {
    return new AuthApiError(
      "Çok fazla giriş denemesi yapıldı. Lütfen 15 dakika bekleyip tekrar deneyin.",
      { status, code, requiresVerification }
    );
  }

  if (status === 403 && requiresVerification) {
    return new AuthApiError(
      "Giriş için email doğrulaması gerekli. Doğrulama mailini tekrar gönderebilirsiniz.",
      { status, code, requiresVerification }
    );
  }

  const errorMessage =
    data?.message ||
    data?.error ||
    err.message ||
    "Giriş başarısız. Lütfen tekrar deneyin.";

  return new AuthApiError(errorMessage, {
    status,
    code,
    requiresVerification,
  });
}

function mapRegisterError(error: unknown): ErrorResponse {
  const err = error as ApiErrorShape;
  const data = err?.response?.data || {};
  const status = err?.response?.status;
  const code = data?.code;
  const rawMessage = String(data?.message || data?.error || "").toLowerCase();

  if (
    code === "EMAIL_ALREADY_EXISTS" ||
    rawMessage.includes("email already exists")
  ) {
    return {
      error: "Çakışma",
      code: "EMAIL_ALREADY_EXISTS",
      message:
        "Bu email adresi zaten kayıtlı. Giriş yapabilir veya farklı bir email adresi deneyebilirsiniz.",
    };
  }

  if (
    code === "PHONE_ALREADY_EXISTS" ||
    rawMessage.includes("phone already exists")
  ) {
    return {
      error: "Çakışma",
      code: "PHONE_ALREADY_EXISTS",
      message:
        "Bu telefon numarası sistemde zaten kayıtlı. Lütfen farklı bir numara girin.",
    };
  }

  if (status === 409) {
    return {
      error: "Çakışma",
      message:
        "Bu bilgilerle daha önce kayıt oluşturulmuş görünüyor. Lütfen bilgilerinizi kontrol edin.",
    };
  }

  return {
    error: String(data?.error || "Bağlantı Hatası"),
    message:
      String(data?.message || "").trim() ||
      "Sunucuya bağlanırken bir hata oluştu. Lütfen tekrar deneyin.",
    code: data?.code,
    details: data?.details,
  };
}

/**
 * Register a new user with organization
 */
export interface RegisterParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName?: string;
  phone?: string;
  termsAccepted: boolean;
  usageMode?: "BUSINESS" | "INDIVIDUAL";
  industry?: string;
  size?: string;
  address?: string;
  domain?: string;
}

export interface GoogleRegisterParams {
  credential: string;
  termsAccepted: boolean;
  usageMode?: "BUSINESS" | "INDIVIDUAL";
  organizationName?: string;
}

export interface GoogleLoginParams {
  credential: string;
}

export interface GoogleOAuthStartParams {
  mode: "login" | "register";
  usageMode?: "BUSINESS" | "INDIVIDUAL";
  organizationName?: string;
  termsAccepted?: boolean;
  returnTo?: string;
  conversionEventId?: string;
}

export interface RegisterEmailAvailabilityResponse {
  available: boolean;
  message: string | null;
}

export async function register(params: RegisterParams): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>(
      "/api/v1/auth/register",
      {
        email: params.email,
        password: params.password,
        firstName: params.firstName,
        lastName: params.lastName,
        organizationName: params.organizationName,
        phone: params.phone,
        termsAccepted: params.termsAccepted,
        usageMode: params.usageMode || "BUSINESS",
        industry: params.industry,
        size: params.size,
        address: params.address,
        domain: params.domain,
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw mapRegisterError(error);
  }
}

export async function registerWithGoogle(
  params: GoogleRegisterParams
): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>(
      "/api/v1/auth/register/google",
      {
        credential: params.credential,
        termsAccepted: params.termsAccepted,
        usageMode: params.usageMode || "BUSINESS",
        organizationName: params.organizationName,
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw mapRegisterError(error);
  }
}

export async function loginWithGoogle(
  params: GoogleLoginParams
): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>(
      "/api/v1/auth/login/google",
      {
        credential: params.credential,
      }
    );
    return {
      ...response.data,
      token: response.data.token || response.data.accessToken,
    };
  } catch (error: unknown) {
    const mapped = mapRegisterError(error);
    throw new AuthApiError(
      mapped.message ||
        "Google ile giriş başarısız oldu. Lütfen tekrar deneyin.",
      {
        status: (error as ApiErrorShape)?.response?.status,
        code: mapped.code,
      }
    );
  }
}

export function buildGoogleOAuthStartUrl(
  params: GoogleOAuthStartParams
): string {
  const searchParams = new URLSearchParams();
  searchParams.set("mode", params.mode);

  if (params.usageMode) {
    searchParams.set("usageMode", params.usageMode);
  }

  if (params.organizationName?.trim()) {
    searchParams.set("organizationName", params.organizationName.trim());
  }

  if (typeof params.termsAccepted === "boolean") {
    searchParams.set("termsAccepted", String(params.termsAccepted));
  }

  if (params.returnTo) {
    searchParams.set("returnTo", params.returnTo);
  }

  if (params.conversionEventId) {
    searchParams.set("conversionEventId", params.conversionEventId);
  }

  return `/api/v1/auth/google/start?${searchParams.toString()}`;
}

export async function checkRegisterEmailAvailability(
  email: string
): Promise<RegisterEmailAvailabilityResponse> {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: RegisterEmailAvailabilityResponse;
    }>("/api/v1/auth/register/email-availability", {
      params: { email },
    });

    return response.data.data;
  } catch (error: unknown) {
    const mapped = mapRegisterError(error);
    throw new AuthApiError(
      mapped.message ||
        "E-posta uygunluk kontrolü tamamlanamadı. Lütfen tekrar deneyiniz."
    );
  }
}

/**
 * Login user
 */
export async function login(
  email: string,
  password: string
): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>("/api/v1/auth/login", {
      email,
      password,
    });
    return {
      ...response.data,
      token: response.data.token || response.data.accessToken,
    };
  } catch (error: unknown) {
    throw mapLoginError(error);
  }
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    await requestCookieLogout();
  } catch (error: unknown) {
    const err = error as ApiErrorShape;
    // Logout is idempotent: 401/403 means session already invalid/ended.
    if (err?.response?.status !== 401 && err?.response?.status !== 403) {
      throw error;
    }
  } finally {
    // Always clear user cache on logout
    localStorage.removeItem("auth_user");
  }
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User> {
  try {
    const response = await apiClient.get<{ user: User }>("/api/v1/auth/me");
    return response.data.user;
  } catch (error: unknown) {
    const err = error as ApiErrorShape;
    const code = err?.response?.data?.code;
    if (code === "refresh_in_progress_retry" || code === "refresh_race_retry") {
      throw new AuthApiError(
        "Oturum yenileme başka bir sekmede devam ediyor. Lütfen kısa süre sonra tekrar deneyin.",
        { status: 409, code }
      );
    }
    if (code === "user_cache_missing") {
      throw new AuthApiError(
        "Oturum önbelleği yenilendi. Lütfen tekrar giriş yapın.",
        { status: 401, code }
      );
    }
    throw (
      err.response?.data || {
        error: "Bağlantı Hatası",
        message: "Kullanıcı bilgileri alınamadı.",
      }
    );
  }
}

/**
 * Refresh cookie-backed session before protected bootstrap calls.
 */
export async function silentRefreshSession(): Promise<boolean> {
  const nextToken = await refreshCookieAuthSession({ emitSync: false });
  return Boolean(nextToken);
}

/**
 * Refresh token
 */
export async function refreshToken(): Promise<string> {
  try {
    const response = await apiClient.post<RefreshTokenResponse>(
      "/api/v1/auth/refresh"
    );
    const {
      token,
      accessToken,
      migratedFromLegacyToken: _migratedFromLegacyToken,
    } = response.data;
    const nextToken = token || accessToken;
    if (!nextToken) {
      throw new AuthApiError("Yeni access token alınamadı.", {
        status: 500,
        code: "refresh_missing_access_token",
      });
    }
    return nextToken;
  } catch (error: unknown) {
    const err = error as ApiErrorShape;
    throw (
      err.response?.data || {
        error: "Bağlantı Hatası",
        message: "Oturum yenilenemedi.",
      }
    );
  }
}

export async function requestPasswordReset(
  email: string,
  captchaToken: string
): Promise<ForgotPasswordResponse> {
  try {
    const response = await apiClient.post<ForgotPasswordResponse>(
      "/api/v1/auth/forgot-password",
      {
        email,
        captchaToken,
      }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiErrorShape;
    const status = err?.response?.status;
    const data = err?.response?.data;
    const code = data?.code;

    if (status === 429) {
      throw new AuthApiError(
        "Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.",
        { status, code }
      );
    }

    const details = Array.isArray(data?.details)
      ? (data.details as unknown[])
      : [];
    const hasCaptchaValidationError = details.some((item: unknown) => {
      if (!item || typeof item !== "object") return false;
      const path = (item as { path?: string }).path;
      return path === "captchaToken";
    });
    if (status === 400 && hasCaptchaValidationError) {
      throw new AuthApiError("Lütfen captcha doğrulamasını tamamlayın.", {
        status,
        code,
      });
    }

    if (code === "captcha_failed" || code === "captcha_missing") {
      const turnstileCodes = Array.isArray(
        (data as { details?: { turnstileErrorCodes?: string[] } })?.details
          ?.turnstileErrorCodes
      )
        ? ((data as { details?: { turnstileErrorCodes?: string[] } }).details
            ?.turnstileErrorCodes ?? [])
        : [];
      const debugSuffix =
        process.env.NODE_ENV !== "production" && turnstileCodes.length > 0
          ? ` (DEV detay: ${turnstileCodes.join(", ")})`
          : "";
      throw new AuthApiError(
        `Captcha doğrulaması başarısız. Lütfen tekrar deneyin.${debugSuffix}`,
        { status, code }
      );
    }

    if (code === "captcha_service_unavailable") {
      throw new AuthApiError(
        "Captcha servisi şu anda kullanılamıyor. Lütfen kısa süre sonra tekrar deneyin.",
        { status, code }
      );
    }

    throw new AuthApiError(
      data?.message || "Şifre sıfırlama talebi gönderilemedi.",
      { status, code }
    );
  }
}

type AuthErrorData = NonNullable<ApiErrorShape["response"]>["data"];

function hasCaptchaTokenValidationError(data: AuthErrorData): boolean {
  const details = Array.isArray(data?.details)
    ? (data.details as unknown[])
    : [];
  return details.some((item) =>
    Boolean(
      item &&
      typeof item === "object" &&
      (item as { path?: string }).path === "captchaToken"
    )
  );
}

function getTurnstileErrorCodes(data: AuthErrorData): string[] {
  const codes = (data as { details?: { turnstileErrorCodes?: string[] } })
    ?.details?.turnstileErrorCodes;
  return Array.isArray(codes) ? codes : [];
}

function throwResetPasswordError(error: unknown): never {
  const err = error as ApiErrorShape;
  const status = err?.response?.status;
  const data = err?.response?.data;
  const code = data?.code;
  const options = { status, code };
  if (status === 429)
    throw new AuthApiError(
      "Çok fazla deneme yapıldı. Lütfen daha sonra tekrar deneyin.",
      options
    );
  if (status === 400 && hasCaptchaTokenValidationError(data))
    throw new AuthApiError("Lütfen captcha doğrulamasını tamamlayın.", options);
  if (code === "invalid_or_expired_token")
    throw new AuthApiError(
      "Bağlantı geçersiz veya süresi dolmuş. Lütfen yeni bir şifre sıfırlama talebi oluşturun.",
      options
    );
  if (code === "weak_password")
    throw new AuthApiError(
      "Şifre gereksinimleri karşılanmıyor. Lütfen daha güçlü bir şifre deneyin.",
      options
    );
  if (code === "captcha_failed" || code === "captcha_missing") {
    const codes = getTurnstileErrorCodes(data);
    const suffix =
      process.env.NODE_ENV !== "production" && codes.length > 0
        ? ` (DEV detay: ${codes.join(", ")})`
        : "";
    throw new AuthApiError(
      `Captcha doğrulaması başarısız. Lütfen tekrar deneyin.${suffix}`,
      options
    );
  }
  if (code === "captcha_service_unavailable")
    throw new AuthApiError(
      "Captcha servisi şu anda kullanılamıyor. Lütfen kısa süre sonra tekrar deneyin.",
      options
    );
  throw new AuthApiError(
    data?.message || "Şifre güncellenemedi. Lütfen tekrar deneyin.",
    options
  );
}

export async function resetPasswordWithToken(
  token: string,
  newPassword: string,
  captchaToken: string
): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>(
      "/api/v1/auth/reset-password",
      {
        token,
        newPassword,
        captchaToken,
      }
    );
    return {
      ...response.data,
      token: response.data.token || response.data.accessToken,
    };
  } catch (error: unknown) {
    throwResetPasswordError(error);
  }
}

export default apiClient;
